import React from 'react'
import { useDispatch } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'next-i18next'

import Bugsnag, { NotifiableError } from '@bugsnag/js'

import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'

import { RootState, TypedDispatch } from 'redux/store'

import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import StripeCheckout from './components/stripe-checkout'
import useFlow from 'modules/flow/state/use-flow'
import Paragraph from 'modules/ui/atoms/Typography/Paragraph'
import {
  trackFormErrorEvent,
  trackFormSubmitEvent,
} from '../../../analytics/helpers/track-form-events'
import { FormWithoutStyles } from '../shared/form.styled'
import { setIsAmex } from 'redux/features/paymentDetails'
import { StatusType } from 'modules/status/state/status-types'
import useStatus from 'modules/status/state/use-status'

import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'

const CardDetailsStep = () => {
  const flow = useFlow()
  const status = useStatus()

  const trackEvent = useTrackUIElementEvent()

  const dispatch = useDispatch<TypedDispatch<RootState>>()
  const { t } = useTranslation('common')

  const paymentMethodRef = React.useRef<{
    savePaymentMethodToStore: () => Promise<void | Error>
  }>()

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false, // handle error focusing manually for Stripe
  })

  const { handleSubmit } = methods

  const handleCardBrand = (brand: string) => {
    // stripe uses 'amex', tokenex uses 'americanExpress'
    dispatch(setIsAmex(brand === 'amex' || brand === 'americanExpress'))
  }

  /**
   * On submit the following will happen:
   *  1) We'll validate that a payment method has been registed via the react component reference.
   *  2) If so, we'll update the RTK store to contain the billing details
   *  3) Then we'll call the rendered payment methods `savePaymentMethodToStore` method
   *  Once the checkout flow is complete, the saved payment method is used to finalize the payment.
   *
   * @param data
   */
  const onSubmit = async () => {
    trackFormSubmitEvent(flow.currentStep.name)

    trackEvent('Donation Payment Details Submitted', mParticleSdk.EventType.Other)

    try {
      // dispatch(setStatus({ status: OrderStatus.SUBMITTING }))

      if (paymentMethodRef.current) {
        const error = await paymentMethodRef.current.savePaymentMethodToStore()

        if (error) {
          trackFormErrorEvent(flow.currentStep.name, error.message)

          status.set(StatusType.FAILED, error.message)

          return
        }
      } else {
        throw new Error('An error has occurred while trying to validate your payment method.')
      }

      const nextStep = flow.nextStep

      if (nextStep) {
        flow.goToStep(nextStep.name)
      }

      status.set(StatusType.IDLE)
    } catch (e) {
      console.error(e)

      Bugsnag.notify(e as NotifiableError)

      status.set(
        StatusType.FAILED,
        t(
          'CardDetailsStep.errorValidatePaymentMethod',
          'An error has occurred while trying to validate your payment method.',
        ),
      )
    }
  }

  /**
   * Get the selected processor (BRAINTREE, WEPAY, STRIPE, etc) and render in
   * the appropriate CC input component.
   *
   * TODO: I believe we're only supporting Stripe credit cards for MVP. So TokenEx
   * may be a future implementation.
   */

  /**
   * We'll likely want the CC input component to have an interface that can be used
   * by this component for setting the appropriate payment method data when the user
   * fills out their CC (source, intent, payment token, etc.)
   */

  return (
    <FormProvider {...methods}>
      <FormWithoutStyles onSubmit={handleSubmit(onSubmit)}>
        <FlowTwoColumnStepLayout.Header
          titleSize={'sm'}
          title={flow.currentStep.title}
          backButtonDataId="card-details-step-back-button"
        />
        <FlowTwoColumnStepLayout.Body>
          <Paragraph style={{ textAlign: 'center', marginBottom: 16, marginTop: 16 }}>
            {t(
              'CardDetailsStep.paragraph.enterCardDetails',
              'Entering your card details allows us to process your donation. This card will be charged.',
            )}
          </Paragraph>
          <Column>
            <Row>
              <Column>
                <StripeCheckout ref={paymentMethodRef} onCardBrand={handleCardBrand} />
              </Column>
            </Row>
          </Column>
        </FlowTwoColumnStepLayout.Body>
        <FlowTwoColumnStepLayout.Footer>
          <FlowTwoColumnStepLayout.FooterButton
            size="lg"
            type="submit"
            dataId="card-details-step-continue-button"
            disabled={
              status.type === StatusType.SUBMITTING || status.type === StatusType.PROCESSING
            }
          >
            {t('CardDetailsStep.footerButton.continue', 'Continue')}
          </FlowTwoColumnStepLayout.FooterButton>
        </FlowTwoColumnStepLayout.Footer>
      </FormWithoutStyles>
    </FormProvider>
  )
}

export default CardDetailsStep
